import {
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  TableCell,
  TableRow,
  TextField,
} from '@mui/material';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { availabilityOptions } from '../../../constants';
import { useFetchStockLimit } from '../../../hooks/useFetchStockLimit';

const priceStyle = { py: 1, width: 70 };

// Locale-aware number formatting for Greek (el-GR)
const LOCALE = 'el-GR';
const numberFormatter = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Get the decimal separator for the locale
const getDecimalSeparator = () => {
  const parts = numberFormatter.formatToParts(1.1);
  return parts.find((part) => part.type === 'decimal')?.value || ',';
};

const decimalSeparator = getDecimalSeparator();

// Format number using locale-aware formatter
const formatPrice = (num, allowZero = false) => {
  if (num === null || num === undefined || num === '' || isNaN(num)) return '';
  if (num === 0 && !allowZero) return '';
  return numberFormatter.format(num);
};

// Parse locale-aware number input (handles both comma and period for flexibility)
const parsePrice = (value) => {
  if (value === '' || value === decimalSeparator) return null;
  
  // Normalize: replace locale decimal separator and period with period for parsing
  let normalized = value;
  if (decimalSeparator !== '.') {
    normalized = normalized.replace(decimalSeparator, '.');
  }
  normalized = normalized.replace(',', '.'); // Handle comma as fallback
  
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? null : parsed;
};

const VariantTable = ({ variant, setProductVariant }) => {
  const stockLimit = useFetchStockLimit();

  const handlePriceChange = (variantId, value) => {
    // Normalize input: convert period to locale decimal separator
    const normalizedInput = value.replace('.', decimalSeparator);
    
    // Allow empty or just decimal separator
    if (normalizedInput === '' || normalizedInput === decimalSeparator) {
      setProductVariant((prevVariants) => ({
        ...prevVariants,
        variations: prevVariants?.variations?.map((v) =>
          v.variationid === variantId
            ? {
                ...v,
                skroutz_price_with_vat: 0,
                _rawPriceInput: normalizedInput, // Store raw input to prevent cursor jump
              }
            : v
        ),
      }));
      return;
    }

    // Parse and validate (don't format during typing to prevent cursor jump)
    const parsedValue = parsePrice(normalizedInput);
    
    if (parsedValue === null || parsedValue < 0) {
      return; // Invalid input, don't update
    }

    setProductVariant((prevVariants) => ({
      ...prevVariants,
      variations: prevVariants?.variations?.map((v) =>
        v.variationid === variantId
          ? {
              ...v,
              skroutz_price_with_vat: parsedValue,
              _rawPriceInput: normalizedInput, // Store raw input to prevent cursor jump
            }
          : v
      ),
    }));
  };

  const handlePriceBlur = (variantId, value) => {
    // Format only on blur
    const parsedValue = parsePrice(value);
    
    if (parsedValue === null || parsedValue < 0) {
      setProductVariant((prevVariants) => ({
        ...prevVariants,
        variations: prevVariants?.variations?.map((v) =>
          v.variationid === variantId
            ? {
                ...v,
                skroutz_price_with_vat: 0,
                _rawPriceInput: undefined, // Clear raw input
              }
            : v
        ),
      }));
      return;
    }

    // Round to 2 decimal places
    const formattedValue = Math.round(parsedValue * 100) / 100;

    setProductVariant((prevVariants) => ({
      ...prevVariants,
      variations: prevVariants?.variations?.map((v) =>
        v.variationid === variantId
          ? {
              ...v,
              skroutz_price_with_vat: formattedValue,
              _rawPriceInput: undefined, // Clear raw input, will use formatted
            }
          : v
      ),
    }));
  };

  const handleSyncPrices = (variantId, value) => {
    setProductVariant((prevVariants) => ({
      ...prevVariants,
      variations: prevVariants?.variations?.map((v) =>
        v.variationid !== variantId ? { ...v, skroutz_price_with_vat: value } : v
      ),
    }));
  };

  const handleAvailabilityChange = (variantId, value) =>
    setProductVariant((prevVariants) => ({
      ...prevVariants,
      variations: prevVariants?.variations?.map((v) =>
        v.variationid === variantId ? { ...v, availability: value } : v
      ),
    }));

  const handleSendToSkroutzChange = (variantId, checked) =>
    setProductVariant((prevVariants) => ({
      ...prevVariants,
      variations: prevVariants?.variations?.map((v) => (v.variationid === variantId ? { ...v, enabled: checked } : v)),
    }));

  const handleQuantityChange = (variantId, value) => {
    let parsedValue = parseInt(value);

    if (isNaN(parsedValue)) parsedValue = 0;

    setProductVariant((prevVariants) => ({
      ...prevVariants,
      variations: prevVariants?.variations?.map((v) => {
        if (v.variationid !== variantId) return v;

        const newManualQuantity = parsedValue;
        const newSkroutzQuantity =
          newManualQuantity === 0
            ? (v.initial_skroutz_quantity ?? v.skroutz_quantity)
            : Math.min(newManualQuantity, v.shop_quantity);

        return {
          ...v,
          manual_quantity: newManualQuantity,
        };
      }),
    }));
  };

  const getSkroutzQuantity = () => {
    let initialQuantity = 0;

    const { manual_quantity, shop_quantity } = variant;

    if (manual_quantity > 0) {
      initialQuantity = Math.min(shop_quantity, manual_quantity);
    } else if (manual_quantity === 0) {
      if (shop_quantity >= stockLimit) initialQuantity = shop_quantity;
      if (shop_quantity < stockLimit) initialQuantity = 0;
    }

    return initialQuantity;
  };

  const getSkroutzStartPrice = () => {
    const { price_with_vat, sales_price_with_vat, skroutz_price_with_vat } = variant;

    if (skroutz_price_with_vat > 0) {
      return skroutz_price_with_vat;
    }

    const candidates = [price_with_vat, sales_price_with_vat].filter((v) => v > 0);
    return candidates.length > 0 ? Math.min(...candidates) : 0;
  };

  // Only highlight if skroutz price is different from initial/sales price
  // Compare numeric values (already in locale format internally)
  const getSkroutzPriceColor = () => {
    const skroutzPrice = variant.skroutz_price_with_vat || 0;
    const initialPrice = variant.price_with_vat || 0;
    const salesPrice = variant.sales_price_with_vat || 0;

    if (skroutzPrice === 0) {
      return 'inherit';
    }

    // Round to 2 decimal places for comparison to avoid floating point issues
    const roundedSkroutz = Math.round(skroutzPrice * 100) / 100;
    const roundedInitial = Math.round(initialPrice * 100) / 100;
    const roundedSales = Math.round(salesPrice * 100) / 100;

    // Highlight only if different from both initial and sales price
    if (roundedSkroutz !== roundedInitial && roundedSkroutz !== roundedSales) {
      return '#A10E9C';
    }

    return 'inherit';
  };

  const skroutzPriceColor = getSkroutzPriceColor();

  return (
    <TableRow>
      <TableCell style={{ fontWeight: 'bold' }}>{variant.size}</TableCell>
      <TableCell>{variant.ean}</TableCell>

      {/* Quantity */}

      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
        <div>Shop:</div>
        <div>{variant.shop_quantity}</div>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
        <div>on Skroutz:</div>
        <div>{getSkroutzQuantity()}</div>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <TextField
          label="Manual"
          value={variant.manual_quantity}
          onChange={(e) => handleQuantityChange(variant.variationid, e.target.value)}
          type="number"
          variant="outlined"
          sx={{ '& .MuiInputBase-input': { py: 1, width: 60 } }}
          inputProps={{ min: 0 }}
        />
      </TableCell>

      {/* Prices */}
      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
        <div>Initial price:</div>
        <div>{formatPrice(variant.price_with_vat, true)}€</div>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
        <div>After %:</div>
        <div>{formatPrice(variant.sales_price_with_vat, true)}€</div>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <TextField
          label="skroutz"
          value={
            variant._rawPriceInput !== undefined
              ? variant._rawPriceInput
              : (() => {
                  const price = variant.skroutz_price_with_vat > 0 
                    ? variant.skroutz_price_with_vat 
                    : getSkroutzStartPrice();
                  return formatPrice(price);
                })()
          }
          onChange={(e) => handlePriceChange(variant.variationid, e.target.value)}
          onBlur={(e) => handlePriceBlur(variant.variationid, e.target.value)}
          type="text"
          size="large"
          variant="outlined"
          sx={{
            '& .MuiInputBase-input': {
              py: 1,
              width: 80,
              color: skroutzPriceColor,
            },
            // '& .MuiFormLabel-root': { color: skroutzPriceColor },
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start">€</InputAdornment>,
          }}
          inputProps={{
            inputMode: 'decimal',
            pattern: '[0-9]*[,.]?[0-9]*',
          }}
        />
        <IconButton
          color="success"
          onClick={() => handleSyncPrices(variant.variationid, variant.skroutz_price_with_vat)}
        >
          <ImportExportIcon />
        </IconButton>
      </TableCell>

      <TableCell>
        <FormControl variant="outlined" size="small" sx={{ width: 150 }}>
          <Select
            variant="outlined"
            value={variant.availability}
            onChange={(e) => handleAvailabilityChange(variant.variationid, e.target.value)}
            displayEmpty
          >
            {availabilityOptions.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }} align="center">
        <Switch
          checked={variant.enabled}
          onChange={(e) => handleSendToSkroutzChange(variant.variationid, e.target.checked)}
          color="success"
        />
        {variant.enabled && <span>Sent</span>}
      </TableCell>
    </TableRow>
  );
};

export default VariantTable;
